import React from 'react';
import { Typography, Grid } from '@material-ui/core';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';

import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import moment from 'moment';
import MUIDataTable from 'mui-datatables';
import { makeSelectAuditLogs } from '../../selectors/clients';
import { selectAccess, selectAuthClients } from '../../selectors/auth';

const rowPropType = PropTypes.shape({
  entityId: PropTypes.number,
  entityName: PropTypes.string,
  operationType: PropTypes.string,
  userId: PropTypes.string,
  userName: PropTypes.string,
  createdAt: PropTypes.string,
  associatedClients: PropTypes.arrayOf(PropTypes.number),
  value: PropTypes.string,
  oldAssociatedClients: PropTypes.arrayOf(PropTypes.number),
  oldValue: PropTypes.string,
  oldEntityId: PropTypes.number,
  oldEntityName: PropTypes.string,
});

const useStyles = makeStyles(theme => ({
  wrapper: {
    paddingTop: theme.spacing(3),
  },
  sectionTitle: {
    borderBottom: '2px solid #DFDFDF',
    padding: theme.spacing(3),
  },
  root: {
    '& > *': {
      borderBottom: 'unset',
    },
  },
  collapseBoxValueContainer: {
    margin: '1em',
  },
  collapseBoxValueRow: {
    padding: '.25em',
  },
  different: {
    color: theme.palette.secondary.main,
  },
}));

function showOldValue(row) {
  const { entityId, entityName, oldValue, oldEntityId, oldEntityName } = row;

  const entitiesMatch = entityId === oldEntityId && entityName === oldEntityName;
  return JSON.parse(oldValue) && entitiesMatch;
}

function Row(props) {
  const { row } = props;
  const classes = useStyles();

  const { associatedClients, oldAssociatedClients, value, oldValue } = row;

  const valueObj = JSON.parse(value);
  valueObj.associatedClients = `[ ${associatedClients.join(', ')} ]`;
  const oldValueObj = JSON.parse(oldValue);

  if (oldValueObj && oldAssociatedClients)
    oldValueObj.associatedClients = `[ ${oldAssociatedClients.join(', ')} ]`;

  return (
    <TableRow>
      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
        <Grid container direction="row">
          <Grid item xs={12} sm={6}>
            <Typography variant="h5" gutterBottom component="div">
              Value
            </Typography>
            <Grid container direction="row" className={classes.collapseBoxValueContainer}>
              {Object.keys(valueObj).map(ky => (
                <Grid item xs={12} className={classes.collapseBoxValueRow}>
                  <b>{ky}</b>: {valueObj[ky]?.toString()}
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="h5" gutterBottom component="div">
              Old Value
            </Typography>
            <Grid container direction="row" className={classes.collapseBoxValueContainer}>
              {showOldValue(row) &&
                Object.keys(oldValueObj).map(ky => (
                  <Grid
                    item
                    xs={12}
                    className={`${classes.collapseBoxValueRow} ${
                      oldValueObj[ky] !== valueObj[ky] ? classes.different : ''
                    }`}
                  >
                    <b>{ky}</b>: {oldValueObj[ky]?.toString()}
                  </Grid>
                ))}
            </Grid>
          </Grid>
        </Grid>
      </TableCell>
    </TableRow>
  );
}

Row.defaultProps = {
  row: {},
};

Row.propTypes = {
  row: rowPropType,
};

function ClientAuditLogsTable(props) {
  const { selectedClient, auditLogs, access, authClients } = props;

  const typeConverter = {
    POST: 'Create',
    PUT: 'Update',
    DELETE: 'Delete',
  };

  const data = auditLogs
    .map(log => {
      const { operationType, entityName, createdAt, oldEntityName, ...row } = log;
      return {
        entityName: entityName.split('/').pop(),
        oldEntityName: oldEntityName?.split('/').pop(),
        operationType: typeConverter[operationType],
        createdAt: moment(createdAt).format('lll'),
        ...row,
      };
    })
    .filter(log => {
      const { operationType, ...otherValues } = log;
      return (
        operationType !== 'Update' || (operationType === 'Update' && showOldValue(otherValues))
      );
    });

  const columns = [
    { name: 'entityId', label: 'EntityId' },
    { name: 'entityName', label: 'EntityName' },
    { name: 'operationType', label: 'Type' },
    { name: 'userId', label: 'UserId' },
    { name: 'userName', label: 'UserName' },
    { name: 'createdAt', label: 'CreatedAt' },
    {
      name: 'associatedClients',
      label: 'AssociatedClients',
      options: { display: false, filter: false, viewColumns: false },
    },
    {
      name: 'value',
      label: 'Value',
      options: { display: false, filter: false, viewColumns: false },
    },
    {
      name: 'oldAssociatedClients',
      label: 'OldAssociatedClients',
      options: { display: false, filter: false, viewColumns: false },
    },
    {
      name: 'oldValue',
      label: 'OldValue',
      options: { display: false, filter: false, viewColumns: false },
    },
  ];

  const options = {
    filterType: 'checkbox',
    expandableRows: true,
    print: false,
    selectableRowsHeader: false,
    selectableRowsHideCheckboxes: true,
    viewColumns: false,
    downloadOptions: {
      filterOptions: {
        useDisplayedRowsOnly: true,
      },
    },
    renderExpandableRow: (rowData, rowMeta) => <Row row={data[rowMeta.rowIndex]} />,
  };

  const showTable =
    access.isInternal || (authClients.contains(selectedClient.id) && access.isAdmin);

  const classes = useStyles();
  return (
    <Grid className={classes.grid} maxwidth="lg">
      <Paper>
        {showTable ? (
          <MUIDataTable title="Audit Logs" data={data} columns={columns} options={options} />
        ) : null}
      </Paper>
    </Grid>
  );
}

ClientAuditLogsTable.defaultProps = {
  selectedClient: {},
  authClients: [],
  access: {},
  auditLogs: [],
};

ClientAuditLogsTable.propTypes = {
  selectedClient: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    billingAddressLine1: PropTypes.string,
    billingAddressLine2: PropTypes.string,
    billingAddressZip: PropTypes.string,
    billingAddressState: PropTypes.string,
    billingAddressCity: PropTypes.string,
    users: PropTypes.arrayOf(
      PropTypes.shape({
        createdAt: PropTypes.string,
        email: PropTypes.string,
        id: PropTypes.number,
      })
    ),
  }),
  authClients: PropTypes.arrayOf(PropTypes.number),
  access: PropTypes.shape({
    isInternal: PropTypes.bool,
    isAdmin: PropTypes.bool,
  }),
  auditLogs: PropTypes.arrayOf(rowPropType),
};

const mapStateToProps = createStructuredSelector({
  auditLogs: makeSelectAuditLogs(),
  access: selectAccess(),
  authClients: selectAuthClients(),
});

const mapDispatchToProps = () => {};

export default connect(mapStateToProps, mapDispatchToProps)(ClientAuditLogsTable);
